import {
  onChildAdded,
  onDisconnect,
  onValue,
  push,
  ref,
  remove,
  serverTimestamp,
  set,
  type Database,
  type Unsubscribe,
} from 'firebase/database';

import {
  CONTROL_CHANNEL_LABEL,
  SESSIONS_ROOT,
  encodeControlMessage,
  encodeInputMessage,
  parseControlMessage,
  parseIceCandidatePayload,
  parseSdpPayload,
  sessionAnswerPath,
  sessionCandidatesPath,
  sessionPath,
  toIceCandidatePayload,
  type InputMessage,
  type RTCIceServerConfig,
  type ScreenOption,
} from '@remote/shared';

export interface StartSessionOptions {
  db: Database;
  deviceId: string;
  clientUid: string;
  log: (line: string) => void;
  onConnectionState: (state: RTCPeerConnectionState) => void;
  onRtt: (milliseconds: number) => void;
  /** Llega cuando el agente empieza a emitir su pantalla. */
  onVideoStream: (stream: MediaStream) => void;
  /** El agente anuncia sus pantallas al abrir el canal (para el selector). */
  onScreens: (screens: ScreenOption[], activeId: string) => void;
  iceServers: RTCIceServerConfig[];
}

export interface ClientSession {
  readonly sessionId: string;
  /** Manda un ping por el DataChannel. El RTT llega por `onRtt`. */
  ping(): void;
  /** Envía un mensaje de input al agente. No hace nada si el canal no está abierto. */
  sendInput(message: InputMessage): void;
  /** Pide al agente cambiar la pantalla capturada. */
  selectScreen(screenId: string): void;
  /** Estadísticas WebRTC en crudo, para el panel de stats. */
  getStats(): Promise<RTCStatsReport>;
  close(): Promise<void>;
}

/**
 * Reordena los códecs del receptor para que H.264 vaya primero.
 *
 * El agente corre en un Mac y Chromium codifica H.264 por hardware
 * (VideoToolbox); VP8/VP9 irían por software y a 8 Mbps con texto eso se nota en
 * la CPU y en la nitidez. `setCodecPreferences` en el transceiver del RECEPTOR
 * decide qué anuncia la oferta, así que la elección se hace aquí, en el cliente.
 * Los códecs auxiliares (rtx, red, ulpfec) se conservan detrás.
 */
function preferH264(transceiver: RTCRtpTransceiver, log: (line: string) => void): void {
  const capabilities = RTCRtpReceiver.getCapabilities('video');
  if (capabilities === null) return;

  const h264 = capabilities.codecs.filter(
    (codec) => codec.mimeType.toLowerCase() === 'video/h264',
  );
  if (h264.length === 0) {
    log('este navegador no ofrece H.264; se negociará lo que haya');
    return;
  }

  const rest = capabilities.codecs.filter(
    (codec) => codec.mimeType.toLowerCase() !== 'video/h264',
  );

  try {
    transceiver.setCodecPreferences([...h264, ...rest]);
  } catch (error) {
    // No es fatal: sin preferencia se negocia el orden por defecto del navegador.
    log(`setCodecPreferences falló — ${describe(error)}`);
  }
}

/**
 * Lado *caller* del handshake. El navegador crea la oferta y el canal de control;
 * el agente contesta.
 */
export async function startSession(options: StartSessionOptions): Promise<ClientSession> {
  const {
    db,
    deviceId,
    clientUid,
    log,
    onConnectionState,
    onRtt,
    onVideoStream,
    onScreens,
    iceServers,
  } = options;

  // `push` sin valor solo reserva una clave, no escribe nada todavía.
  const sessionRef = push(ref(db, SESSIONS_ROOT));
  const sessionId = sessionRef.key;
  if (sessionId === null) {
    throw new Error('RTDB no devolvió clave para la sesión');
  }

  const pc = new RTCPeerConnection({ iceServers });
  const channel = pc.createDataChannel(CONTROL_CHANNEL_LABEL, { ordered: true });
  const subscriptions: Unsubscribe[] = [];

  // El transceiver de vídeo va en la oferta desde el principio: el agente cuelga
  // su pista de él al contestar y todo se negocia en una sola pasada.
  const videoTransceiver = pc.addTransceiver('video', { direction: 'recvonly' });
  preferH264(videoTransceiver, log);

  pc.addEventListener('track', (event) => {
    const stream = event.streams[0] ?? new MediaStream([event.track]);
    log(`pista de ${event.track.kind} recibida`);
    onVideoStream(stream);
  });

  let nextPingId = 1;
  let closed = false;

  channel.addEventListener('open', () => log('DataChannel abierto'));
  channel.addEventListener('close', () => log('DataChannel cerrado'));
  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    const message = parseControlMessage(event.data);
    if (message === null) return;
    switch (message.t) {
      case 'pong':
        // El agente devuelve nuestro propio `ts` sin tocarlo: el RTT se mide con un
        // solo reloj y no hace falta que los dos estén sincronizados.
        onRtt(performance.now() - message.ts);
        return;
      case 'screens':
        onScreens(message.screens, message.activeId);
        return;
      case 'ping':
      case 'select-screen':
        // El cliente no recibe estos; los ignora.
        return;
    }
  });

  pc.addEventListener('connectionstatechange', () => {
    log(`connectionState = ${pc.connectionState}`);
    onConnectionState(pc.connectionState);
  });

  // Los candidatos locales se publican en cuanto aparecen. Los primeros pueden
  // llegar antes de que el `set` de abajo termine, así que se encolan hasta que el
  // nodo de la sesión exista: escribir un hijo de un nodo que aún no cumple las
  // reglas lo rechazaría.
  const pendingLocalCandidates: RTCIceCandidate[] = [];
  let sessionCreated = false;

  const publishCandidate = (candidate: RTCIceCandidate): void => {
    void set(
      push(ref(db, sessionCandidatesPath(sessionId, 'caller'))),
      toIceCandidatePayload(candidate),
    ).catch((error: unknown) => log(`no se pudo publicar un candidato — ${describe(error)}`));
  };

  pc.addEventListener('icecandidate', (event) => {
    if (event.candidate === null) {
      log('fin de candidatos locales');
      return;
    }
    if (sessionCreated) publishCandidate(event.candidate);
    else pendingLocalCandidates.push(event.candidate);
  });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const localDescription = pc.localDescription;
  if (localDescription === null) {
    pc.close();
    throw new Error('setLocalDescription no dejó descripción local');
  }

  // La sesión se crea de una vez, con la oferta dentro. Así el agente nunca ve un
  // nodo a medias: cuando su `onChildAdded` dispara, la oferta ya está ahí.
  await set(sessionRef, {
    deviceId,
    clientUid,
    createdAt: serverTimestamp(),
    state: 'offering',
    offer: { type: 'offer', sdp: localDescription.sdp },
  });

  sessionCreated = true;
  for (const candidate of pendingLocalCandidates) publishCandidate(candidate);
  pendingLocalCandidates.length = 0;

  // Si se cierra la pestaña, el servidor limpia la sesión por nosotros. Sin esto,
  // /sessions se llenaría de basura que el agente tendría que ignorar cada arranque.
  await onDisconnect(sessionRef).remove();

  log(`sesión ${sessionId.slice(-6)} creada, esperando answer`);

  subscriptions.push(
    onValue(ref(db, sessionAnswerPath(sessionId)), (snapshot) => {
      const answer = parseSdpPayload(snapshot.val());
      // Dispara también al suscribirse, cuando todavía no hay answer.
      if (answer === null || answer.type !== 'answer') return;
      if (pc.currentRemoteDescription !== null) return;

      void (async () => {
        try {
          await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
          log('answer aplicada');

          // Suscribirse a los candidatos del agente DESPUÉS de tener descripción
          // remota. `onChildAdded` reproduce los que ya estaban, así que no se
          // pierde ninguno y no hace falta buffer propio.
          subscriptions.push(
            onChildAdded(ref(db, sessionCandidatesPath(sessionId, 'callee')), (candidateSnap) => {
              const candidate = parseIceCandidatePayload(candidateSnap.val());
              if (candidate === null) {
                log('candidato remoto descartado por mal formado');
                return;
              }
              void pc.addIceCandidate(candidate).catch((error: unknown) => {
                log(`addIceCandidate falló — ${describe(error)}`);
              });
            }),
          );
        } catch (error) {
          log(`no se pudo aplicar la answer — ${describe(error)}`);
        }
      })();
    }),
  );

  return {
    sessionId,

    ping(): void {
      if (channel.readyState !== 'open') {
        log(`no se puede hacer ping: el canal está en "${channel.readyState}"`);
        return;
      }
      const id = nextPingId++;
      channel.send(encodeControlMessage({ t: 'ping', id, ts: performance.now() }));
      log(`ping #${String(id)} enviado`);
    },

    sendInput(message: InputMessage): void {
      // El input es de alta frecuencia (los `move` sobre todo); si el canal no
      // está listo se descarta sin ruido en vez de encolar y disparar después.
      if (channel.readyState !== 'open') return;
      channel.send(encodeInputMessage(message));
    },

    selectScreen(screenId: string): void {
      if (channel.readyState !== 'open') return;
      channel.send(encodeControlMessage({ t: 'select-screen', id: screenId }));
    },

    getStats(): Promise<RTCStatsReport> {
      return pc.getStats();
    },

    async close(): Promise<void> {
      if (closed) return;
      closed = true;

      for (const unsubscribe of subscriptions) unsubscribe();
      channel.close();
      pc.close();

      await onDisconnect(sessionRef).cancel();
      await remove(ref(db, sessionPath(sessionId)));
      log(`sesión ${sessionId.slice(-6)} cerrada`);
    },
  };
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
